import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarPlus, CalendarIcon, ArrowRight, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { getNavMonths } from '@/lib/datePickerNav';

interface QuickAddBookingModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: QuickAddBookingData) => void | Promise<void>;
  onMoreOptions?: () => void;
  defaultStartTime?: string;
  defaultEndTime?: string;
  preselectedDate?: Date;
}

export interface QuickAddBookingData {
  description: string;
  startDate: Date;
  endDate: Date;
  startTime?: string;
  endTime?: string;
  isFullDay: boolean;
}

export function QuickAddBookingModal({
  open,
  onClose,
  onSave,
  onMoreOptions,
  defaultStartTime = '09:00',
  defaultEndTime = '17:00',
  preselectedDate,
}: QuickAddBookingModalProps) {
  const [description, setDescription] = useState('');
  const [startDate, setStartDate] = useState<Date | undefined>(preselectedDate || new Date());
  const [endDate, setEndDate] = useState<Date | undefined>(preselectedDate || new Date());
  const [startTime, setStartTime] = useState(defaultStartTime);
  const [endTime, setEndTime] = useState(defaultEndTime);
  const [isFullDay, setIsFullDay] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<{
    startDate?: string;
    endDate?: string;
    startTime?: string;
    endTime?: string;
  }>({});

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setDescription('');
      setStartDate(preselectedDate || new Date());
      setEndDate(preselectedDate || new Date());
      setStartTime(defaultStartTime);
      setEndTime(defaultEndTime);
      setIsFullDay(true);
      setErrors({});
      setIsSaving(false);
    }
  }, [open, preselectedDate, defaultStartTime, defaultEndTime]);

  // Auto-adjust end date when start date changes
  useEffect(() => {
    if (startDate && endDate) {
      const startDateOnly = new Date(startDate);
      startDateOnly.setHours(0, 0, 0, 0);
      const endDateOnly = new Date(endDate);
      endDateOnly.setHours(0, 0, 0, 0);
      if (startDateOnly > endDateOnly) {
        setEndDate(new Date(startDate));
      }
    }
  }, [startDate, endDate]);

  // Real-time end date validation
  useEffect(() => {
    if (startDate && endDate) {
      const startDateOnly = new Date(startDate);
      startDateOnly.setHours(0, 0, 0, 0);
      const endDateOnly = new Date(endDate);
      endDateOnly.setHours(0, 0, 0, 0);
      if (endDateOnly < startDateOnly) {
        setErrors(prev => ({ ...prev, endDate: 'End date cannot be before start date' }));
      } else {
        setErrors(prev => { const e = { ...prev }; delete e.endDate; return e; });
      }
    }
  }, [startDate, endDate]);

  const buildData = (): QuickAddBookingData | null => {
    if (!startDate || !endDate) return null;
    return {
      description,
      startDate,
      endDate,
      startTime: isFullDay ? undefined : startTime,
      endTime: isFullDay ? undefined : endTime,
      isFullDay,
    };
  };

  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};
    if (!startDate) newErrors.startDate = 'Start date is required';
    if (!endDate) newErrors.endDate = 'End date is required';
    if (startDate && endDate) {
      const s = new Date(startDate); s.setHours(0, 0, 0, 0);
      const e = new Date(endDate); e.setHours(0, 0, 0, 0);
      if (e < s) newErrors.endDate = 'End date cannot be before start date';
    }
    if (!isFullDay) {
      if (!startTime) newErrors.startTime = 'Start time is required';
      if (!endTime) newErrors.endTime = 'End time is required';
      const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
      if (startTime && !timeRegex.test(startTime)) newErrors.startTime = 'Invalid time (HH:mm)';
      if (endTime && !timeRegex.test(endTime)) newErrors.endTime = 'Invalid time (HH:mm)';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (isSaving || !validateForm()) return;
    const data = buildData();
    if (!data) return;
    setIsSaving(true);
    try {
      await onSave(data);
      onClose();
    } catch (error) {
      console.error('Failed to save booking:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const formattedPreselectedDate = preselectedDate
    ? format(preselectedDate, 'EEEE, d MMM yyyy')
    : format(new Date(), 'EEEE, d MMM yyyy');

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[420px] p-0 gap-0 overflow-hidden">

        {/* Tinted header band */}
        <div className="bg-primary/5 border-b border-border px-5 py-4">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-sm font-semibold text-foreground">
              <CalendarPlus className="h-4 w-4 text-primary shrink-0" />
              Quick Booking
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground mt-0.5">
              {formattedPreselectedDate}
            </DialogDescription>
          </DialogHeader>
        </div>

        {/* Form body */}
        <div className="px-5 py-4 space-y-4">

          {/* Booking name */}
          <div className="space-y-1.5">
            <Label htmlFor="description" className="text-xs font-medium text-muted-foreground">
              Booking name
            </Label>
            <Input
              id="description"
              placeholder="e.g. Team meeting, Site visit..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="h-9 text-sm"
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSave(); } }}
            />
          </div>

          {/* Date row — start → end */}
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              <div className="flex-1 space-y-1">
                <Label className="text-xs font-medium text-muted-foreground">
                  Start date <span className="text-destructive">*</span>
                </Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        'w-full justify-start text-left font-normal h-9 text-sm px-2.5',
                        !startDate && 'text-muted-foreground',
                        errors.startDate && 'border-destructive'
                      )}
                    >
                      <CalendarIcon className="mr-1.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      {startDate ? format(startDate, 'dd MMM yyyy') : 'Select date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={startDate} defaultMonth={startDate ?? preselectedDate ?? new Date()} onSelect={setStartDate} captionLayout="dropdown" {...getNavMonths(startDate)} initialFocus />
                  </PopoverContent>
                </Popover>
              </div>

              <ArrowRight className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-5" />

              <div className="flex-1 space-y-1">
                <Label className="text-xs font-medium text-muted-foreground">
                  End date <span className="text-destructive">*</span>
                </Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        'w-full justify-start text-left font-normal h-9 text-sm px-2.5',
                        !endDate && 'text-muted-foreground',
                        errors.endDate && 'border-destructive'
                      )}
                    >
                      <CalendarIcon className="mr-1.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      {endDate ? format(endDate, 'dd MMM yyyy') : 'Select date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={endDate}
                      defaultMonth={endDate ?? startDate ?? preselectedDate ?? new Date()}
                      onSelect={setEndDate}
                      captionLayout="dropdown"
                      {...getNavMonths(endDate ?? startDate)}
                      disabled={(date) => {
                        if (startDate) {
                          const s = new Date(startDate); s.setHours(0, 0, 0, 0);
                          const d = new Date(date); d.setHours(0, 0, 0, 0);
                          return d < s;
                        }
                        return false;
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {/* Date errors */}
            {(errors.startDate || errors.endDate) && (
              <p className="flex items-center gap-1 text-xs text-destructive">
                <AlertCircle className="h-3 w-3 shrink-0" />
                {errors.endDate || errors.startDate}
              </p>
            )}
          </div>

          {/* Full day toggle */}
          <div className="flex items-center justify-between rounded-md border border-border bg-muted/30 px-3 py-2.5">
            <div className="space-y-0.5">
              <Label htmlFor="full-day" className="text-sm font-medium cursor-pointer">
                Full day
              </Label>
              <p className="text-xs text-muted-foreground">
                {isFullDay ? '00:00 – 23:59' : 'Set custom times below'}
              </p>
            </div>
            <Switch
              id="full-day"
              checked={isFullDay}
              onCheckedChange={setIsFullDay}
            />
          </div>

          {/* Time fields — animate in when not full day */}
          {!isFullDay && (
            <div className="animate-in slide-in-from-top-1 duration-150 space-y-1.5">
              <div className="flex items-center gap-2">
                <div className="flex-1 space-y-1">
                  <Label htmlFor="start-time" className="text-xs font-medium text-muted-foreground">
                    From <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="start-time"
                    type="time"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    className={cn('h-9 text-sm', errors.startTime && 'border-destructive')}
                  />
                </div>

                <ArrowRight className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-5" />

                <div className="flex-1 space-y-1">
                  <Label htmlFor="end-time" className="text-xs font-medium text-muted-foreground">
                    To <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="end-time"
                    type="time"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                    className={cn('h-9 text-sm', errors.endTime && 'border-destructive')}
                  />
                </div>
              </div>

              {/* Time errors */}
              {(errors.startTime || errors.endTime) && (
                <p className="flex items-center gap-1 text-xs text-destructive">
                  <AlertCircle className="h-3 w-3 shrink-0" />
                  {errors.startTime || errors.endTime}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end border-t border-border px-5 py-3 bg-muted/20">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={onClose} className="h-8">
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              className="h-8 gap-1.5"
              title="Save booking (Enter)"
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'Save Booking'}
              <kbd className="hidden sm:inline-flex items-center rounded border border-primary-foreground/30 bg-primary-foreground/10 px-1 font-mono text-[10px] leading-none">
                ↵
              </kbd>
            </Button>
          </div>
        </div>

      </DialogContent>
    </Dialog>
  );
}
